import { useTranslations } from 'next-intl'

import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import { Icon } from '~/components/atoms/Icon/Icon'
import Link from '~/components/atoms/Link/Link'
import SpecificImage from '~/components/atoms/SpecificImage/SpecificImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TextInput from '~/components/atoms/TextInput/TextInput'
import StarRating from '~/components/blocks/StarRating/StarRating'
import { formatRating } from '~/services/formatRating'

import appReviews from '~~/appGoogleReviews.json'

import styles from './InstantValuationArticleBanner.module.css'

const BANNER_IMAGE = {
  _ref: 'image-759ec7ffdcbb4b9328a5fe064599961687845dd1-1811x1644-png',
  _type: 'reference' as const,
}

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8.39941 1.2002C12.3758 1.2003 15.5996 4.42401 15.5996 8.40039C15.5996 10.0641 15.0327 11.5942 14.085 12.8135L16.6445 15.373L15.3721 16.6455L12.8125 14.0859C11.5933 15.0337 10.0631 15.6005 8.39941 15.6006C4.42303 15.6006 1.19932 12.3768 1.19922 8.40039C1.19922 4.42394 4.42296 1.2002 8.39941 1.2002ZM8.39941 3C5.41708 3 2.99902 5.41805 2.99902 8.40039C2.99913 11.3826 5.41714 13.7998 8.39941 13.7998C11.3816 13.7997 13.7987 11.3826 13.7988 8.40039C13.7988 5.41812 11.3817 3.00011 8.39941 3Z"
      fill="#222222"
    />
  </svg>
)

export interface InstantValuationArticleBannerProps {
  artistName?: string
  onValuationClick?: () => void
}

const InstantValuationArticleBanner = ({
  artistName,
  onValuationClick,
}: InstantValuationArticleBannerProps) => {
  const t = useTranslations('articleContainer')

  return (
    <Link
      href="/valuations"
      className={styles.wrapper}
      onClick={onValuationClick}
    >
      <SpecificImage
        asset={BANNER_IMAGE}
        alt={t('instantValuationBannerTitle')}
        width={298}
        height={200}
        className={styles.image}
      />

      <div className={styles.content}>
        <Text
          Tag="h2"
          weight="bold"
          className={artistName ? styles.titleSmall : styles.titleLarge}
        >
          {artistName
            ? t('instantValuationBannerTitleArtist', { artistName })
            : t('instantValuationBannerTitle')}
        </Text>

        <Text size={TextSizes.bodyMed} className={styles.subCopy}>
          {t('instantValuationBannerSubCopy')}
        </Text>

        <div className={styles.inputs}>
          <div className={styles.inputWrapper}>
            <SearchIcon />
            <TextInput
              placeholder={t('instantValuationBannerArtistPlaceholder')}
              readOnly
              tabIndex={-1}
              className={styles.bannerInput}
            />
          </div>
          <div className={styles.inputWrapper}>
            <SearchIcon />
            <TextInput
              placeholder={t('instantValuationBannerArtworkPlaceholder')}
              readOnly
              tabIndex={-1}
              className={styles.bannerInput}
            />
          </div>
        </div>

        <div className={styles.cta} aria-hidden="true">
          <Button
            variant={ButtonVariants.primaryDark}
            iconLeft
            className={styles.button}
          >
            {t('instantValuationBannerCta')}
          </Button>
        </div>

        {appReviews && (
          <div className={styles.googleSection}>
            <div className={styles.googleTopRow}>
              <Icon type="GoogleMark" width={24} height={24} />
              <Text
                size={TextSizes.primaryTitleLrg}
                className={styles.starRating}
              >
                {formatRating(appReviews?.averageRating)}
              </Text>
              <StarRating
                ratingValue={appReviews?.averageRating || 5}
                id="instant-valuation-banner-reviews"
              />
            </div>
            <Text size={TextSizes.bodySml} className={styles.reviewCount}>
              {t('instantValuationBannerReviews', {
                count: appReviews?.totalReviewCount,
              })}
            </Text>
          </div>
        )}
      </div>
    </Link>
  )
}

export default InstantValuationArticleBanner
